import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, FileImage, PlayCircle, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import ParticleField from "@/components/ParticleField";
import { useBooking } from "@/context/BookingContext";
import { DOCTOR } from "@/data/content";

const ease = [0.16, 1, 0.3, 1];

const AT_REST = { y: 0 };
const MASK_FROM = { y: 28 };
const RISE_14 = { y: 14 };
const RISE_12 = { y: 12 };
const RISE_10 = { y: 10 };
const SCALE_FROM = { scale: 0.97 };
const SCALE_TO = { scale: 1 };
const T_BADGE = { duration: 0.8, ease };
const T_SUB = { duration: 1, delay: 0.8, ease };
const T_LOC = { duration: 1, delay: 0.95, ease };
const T_CTA = { duration: 0.8, delay: 1.1, ease };
const T_CHIP = { duration: 1, delay: 1.3, ease };
const T_IMG = { duration: 1.2, delay: 0.4, ease };
const T_CARD = { duration: 0.8, delay: 1.3, ease };

const MaskedLine = ({ children, delay }) => (
  <span className="block">
    <motion.span
      className="block"
      initial={MASK_FROM}
      animate={AT_REST}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroBackground = () => (
  <>
    <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_85%_-10%,#e8f1fb_0%,#f4f8fd_45%,#ffffff_80%)]" />
    <div className="absolute inset-0 opacity-70">
      <ParticleField density={55} rgb="31, 95, 168" />
    </div>

    {/* Animated glowing nerve pathway */}
    <svg className="absolute inset-0 w-full h-full opacity-[0.22]" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="nerve" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1f5fa8" stopOpacity="0" />
          <stop offset="50%" stopColor="#1f5fa8" stopOpacity="1" />
          <stop offset="100%" stopColor="#1f5fa8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M-50,200 C300,120 500,420 850,300 S1400,250 1800,420"
        fill="none" stroke="url(#nerve)" strokeWidth="1.5"
        strokeDasharray="6 14" className="animate-dash-flow"
      />
      <path
        d="M-50,520 C350,600 600,380 1000,520 S1500,600 1900,480"
        fill="none" stroke="url(#nerve)" strokeWidth="1"
        strokeDasharray="4 18" className="animate-dash-flow"
        style={{ animationDuration: "18s" }}
      />
    </svg>
  </>
);

const DoctorCard = ({ imgY }) => (
  <motion.div style={{ y: imgY }} className="relative z-10 hidden lg:block">
    <motion.div
      initial={SCALE_FROM}
      animate={SCALE_TO}
      transition={T_IMG}
      className="relative animate-float-slow"
    >
      <div className="absolute -inset-8 rounded-full bg-brand/15 blur-3xl" />
      <div className="relative rounded-[2rem] overflow-hidden border border-slate-200 bg-white shadow-[0_30px_80px_-20px_rgba(11,44,77,0.35)]">
        <img
          src={DOCTOR.photo}
          alt="Dr. Arun Reddy M, Endoscopic Spine Surgeon"
          width="520" height="520"
          className="w-full h-auto object-cover"
        />
      </div>

      <motion.div
        initial={RISE_12} animate={AT_REST}
        transition={T_CARD}
        className="absolute -bottom-5 -left-5 bg-white rounded-2xl px-5 py-4 border border-slate-200 shadow-xl"
      >
        <div className="font-heading font-extrabold text-[#0b2c4d] text-base">Dr. Arun Reddy M</div>
        <div className="text-xs text-slate-500 mt-0.5">MBBS · DNB Neurosurgery · FCVS (Japan)</div>
      </motion.div>
    </motion.div>
  </motion.div>
);

const Hero = () => {
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-white pt-[72px]"
    >
      {/* Background layers */}
      <HeroBackground />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 min-h-[calc(100vh-72px)] grid lg:grid-cols-[1.15fr_0.85fr] items-center gap-8 py-12">
        {/* Left: copy */}
        <motion.div style={{ y: textY }} className="relative z-10 max-w-2xl">
          <motion.div
            initial={RISE_14}
            animate={AT_REST}
            transition={T_BADGE}
            className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-light px-4 py-1.5 mb-7"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-glow-pulse" />
            <span className="text-xs font-semibold tracking-[0.15em] uppercase text-brand">
              Neurosurgeon · Endoscopic Spine Surgeon
            </span>
          </motion.div>

          <h1 className="font-heading font-extrabold text-[#0b2c4d] tracking-tighter leading-[0.98] text-4xl sm:text-5xl lg:text-[4.6rem]">
            <MaskedLine delay={0.15}>Advanced</MaskedLine>{" "}
            <MaskedLine delay={0.3}>Endoscopic</MaskedLine>{" "}
            <MaskedLine delay={0.45}>
              <span className="text-brand">Spine Surgery</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={RISE_12} animate={AT_REST}
            transition={T_SUB}
            className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl"
          >
            Personalised evaluation and minimally invasive treatment for selected spine conditions.
          </motion.p>

          <motion.div
            initial={RISE_10} animate={AT_REST}
            transition={T_LOC}
            className="mt-3 flex items-center gap-2 text-slate-500 text-sm"
          >
            <MapPin className="w-4 h-4 text-brand" />
            CARE Hospitals, Gachibowli · Evening clinic at Madinaguda, Hyderabad.
          </motion.div>

          <motion.div
            initial={RISE_14} animate={AT_REST}
            transition={T_CTA}
            className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3"
          >
            <Button
              data-testid="hero-book-btn"
              onClick={() => openBooking("appointment")}
              className="bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 rounded-full group shadow-lg shadow-brand/25"
            >
              Book Appointment
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid="hero-mri-btn"
              onClick={() => openBooking("mri_review")}
              variant="outline"
              className="h-12 px-6 rounded-full border-slate-300 bg-white text-[#0b2c4d] hover:bg-slate-50 hover:text-[#0b2c4d]"
            >
              <FileImage className="w-4 h-4 mr-1" />
              Send MRI for Review
            </Button>
            <a href="#education" data-testid="hero-videos-link">
              <Button variant="ghost" className="h-12 px-4 rounded-full text-slate-600 hover:text-brand hover:bg-brand-light">
                <PlayCircle className="w-4 h-4 mr-1" />
                Watch Patient Videos
              </Button>
            </a>
          </motion.div>

          <motion.a
            href={DOCTOR.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={RISE_10}
            animate={AT_REST}
            transition={T_CHIP}
            data-testid="hero-rating-chip"
            className="mt-8 inline-flex items-center gap-3 group"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={`hero-star-${i}`} className="w-4 h-4 text-amber-400" fill="#fbbf24" />
              ))}
            </div>
            <span className="text-sm text-slate-600 font-semibold group-hover:text-brand transition-colors">
              5.0 ★ — 278 Google Reviews
            </span>
          </motion.a>
        </motion.div>

        {/* Right: doctor photo */}
        <DoctorCard imgY={imgY} />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Scroll</span>
        <div className="w-6 h-10 rounded-full border border-slate-300 flex justify-center pt-2">
          <span className="w-1 h-1 rounded-full bg-brand animate-scroll-dot" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
